import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  CircularProgress,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { AffiliateOrganization, OrganizationUser } from "schemas/dashboard";
import { SetAlertType } from "schemas/functions";

import { fetcherAuth } from "utils/api";

interface Props {
  setAlert: SetAlertType;
  organizationUser: OrganizationUser | null;
}

const AdminAffiliateOrganization = ({ organizationUser, setAlert }: Props) => {
  const { getToken } = useAuth();
  const [affiliateOrg, setAffiliateOrg] =
    useState<AffiliateOrganization | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchAffiliateOrg = async () => {
    if (!organizationUser) return;
    try {
      setLoading(true);
      const res = await fetcherAuth(
        getToken,
        `/api/admin/organizations/${organizationUser?.organizationId}/affiliate-organization`,
        "GET",
      );
      setAffiliateOrg(res.affiliateOrg);
    } catch (error) {
      setAlert(
        error.message || "Unable to fetch affiliate organization",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const approveAffiliateOrganization = async () => {
    try {
      setActionLoading(true);
      const res = await fetcherAuth(
        getToken,
        `/api/admin/organizations/${organizationUser?.organizationId}/affiliate-organization/approve`,
        "POST",
      );
      setAffiliateOrg(res.affiliateOrg);
      setAlert("Succesfully approved affiliate partner", "success");
    } catch (error) {
      setAlert(error.message || "Unable to approve affiliate partner", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const updateAffiliateOrganization = async () => {
    try {
      setActionLoading(true);
      const res = await fetcherAuth(
        getToken,
        `/api/admin/organizations/${organizationUser?.organizationId}/affiliate-organization`,
        "PUT",
        {},
        {
          isActive: affiliateOrg?.isActive,
        },
      );
      setAffiliateOrg(res.affiliateOrg);
      setAlert("Succesfully updated affiliate partner", "success");
    } catch (error) {
      setAlert(error.message || "Unable to update affiliate partner", "error");
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    fetchAffiliateOrg();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationUser?.id]);
  return (
    <>
      <Typography variant="h6" gutterBottom>
        Affiliate Partnership Program
      </Typography>
      {loading ? (
        <CircularProgress />
      ) : !affiliateOrg ? (
        <>
          <Typography paragraph>
            Use the button below to create a coupon code for this user to use as
            an affiliate partner.
          </Typography>
          <LoadingButton
            loading={actionLoading}
            onClick={() => {
              approveAffiliateOrganization();
            }}
            disableElevation
            variant="contained"
          >
            Create Code
          </LoadingButton>
        </>
      ) : (
        <>
          <Typography>
            Coupon Code: <strong>{affiliateOrg.stripePromotionCode}</strong>
          </Typography>
          <Typography paragraph>
            Affiliate Link: <strong>{affiliateOrg.referralLink}</strong>
          </Typography>
          <Box sx={{ mb: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  onChange={() => {
                    setAffiliateOrg((prev) => {
                      if (!prev) {
                        return prev;
                      }
                      return { ...prev, isActive: !prev.isActive };
                    });
                  }}
                  checked={affiliateOrg.isActive}
                />
              }
              label="Affiliate Is Approved and Active"
            />
          </Box>
          <LoadingButton
            loading={actionLoading}
            onClick={() => {
              updateAffiliateOrganization();
            }}
            disableElevation
            variant="contained"
          >
            Update
          </LoadingButton>
        </>
      )}
    </>
  );
};

export default AdminAffiliateOrganization;
